/**
 * UI-side responder for the tofig engine's "image" RPC channel.
 *
 * The plugin sandbox can't measure or decode raw image bytes, so the engine
 * (src/tofig, src/class/MessageChannel) asks the UI — which runs in a real
 * browser — over a small request/response channel:
 *
 *   sandbox: messageChannel.postMessage("image", "getImageResolution", 10000)(url)
 *   →  { scope: "image", name, postMessageId, data: [url] }   (via the relay)
 *   ←  { postMessageId, data: <result> }
 *
 * The sandbox matches the reply by `postMessageId` (see MessageChannel and the
 * `onRaw` wiring in src/plugin/code.ts). This module is the browser end: it
 * answers `getImageResolution` and `atob`. It is purely additive — if it never
 * runs (e.g. an older hosted build), the sandbox call simply times out and the
 * engine falls back to the element's DOM rect, exactly as before.
 */



interface ImageRpcRequest {
  scope?: string;
  name?: string;
  postMessageId?: string;
  data?: any[];
}

/** Measure an image's intrinsic pixel size in the browser. */
function getImageResolution(url: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    // Data URLs only. Loading a remote URL here triggers a CORS-bound network fetch (and taints
    // any later canvas read) — the capture inlines every image as base64, so callers must resolve
    // to those bytes first, never fall back to the remote src.
    if (!url || !url.startsWith('data:')) {
      resolve(null);
      return;
    }
    const img = new Image();
    let settled = false;
    const done = (value: { width: number; height: number } | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => done(null);
    try {
      img.src = url;
      // Cached images can be complete synchronously without firing `onload`.
      if (img.complete && img.naturalWidth) {
        done({ width: img.naturalWidth, height: img.naturalHeight });
      }
    } catch {
      done(null);
    }
  });
}

/** Decode base64 → binary string (the engine's `atob` bridge). */
function decodeBase64(base64: string): string | null {
  try {
    return window.atob(base64);
  } catch {
    return null;
  }
}

/**
 * Downscale/re-encode a data-URL image so it fits Figma's createImage limits
 * (≤4096px per side, PNG/JPEG/GIF only). The sandbox can't touch pixels, so it
 * ships the base64 payload here; the browser decodes it, scales it onto a
 * canvas, and returns a fresh data URL. Doubling as format repair: any format
 * the browser can decode (WebP/AVIF that slipped past the capture transcode)
 * comes back as PNG/JPEG that createImage accepts. Data URLs only — this never
 * touches a remote image URL (CORS taints the canvas and fails the export).
 */
function downscaleImage(dataUrl: string, maxDim: number): Promise<string | null> {
  return new Promise((resolve) => {
    if (!dataUrl.startsWith('data:') || !(maxDim > 0)) {
      resolve(null);
      return;
    }
    const img = new Image();
    let settled = false;
    const done = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) {
          done(null);
          return;
        }
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          done(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // JPEG sources stay JPEG (a photo re-encoded as PNG explodes in size);
        // everything else becomes PNG so transparency survives.
        const jpeg = /^data:image\/jpe?g/i.test(dataUrl);
        done(canvas.toDataURL(jpeg ? 'image/jpeg' : 'image/png', jpeg ? 0.92 : undefined));
      } catch {
        done(null);
      }
    };
    img.onerror = () => done(null);
    try {
      img.src = dataUrl;
    } catch {
      done(null);
    }
  });
}

async function handle(name: string, args: any[]): Promise<any> {
  switch (name) {
    case 'getImageResolution':
      return getImageResolution(String(args[0] ?? ''));
    case 'atob':
      return decodeBase64(String(args[0] ?? ''));
    case 'downscaleImage':
      return downscaleImage(String(args[0] ?? ''), Number(args[1]) || 0);
    default:
      return null;
  }
}

let installed = false;

/** Start answering image-channel RPC requests from the sandbox. Idempotent. */
export function installImageRpc(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('message', (event: MessageEvent) => {
    const req = (event.data as { pluginMessage?: ImageRpcRequest } | null)?.pluginMessage;
    if (!req || req.scope !== 'image' || typeof req.postMessageId !== 'string') return;

    const { postMessageId, name, data } = req;
    void handle(String(name), Array.isArray(data) ? data : []).then((result) => {
      parent.postMessage({ pluginMessage: { postMessageId, data: result } }, '*');
    });
  });
}
